import type { Country } from "./countries";
import { generateLevel, type GeneratedLevel } from "./generator";

export type CardKind = "country" | "capital";
export type GamePhase = "playing" | "won";

export interface Pair {
  id: string;
  country: Country;
}

export interface Card {
  index: number;
  kind: CardKind;
  pairId: string;
  matched: boolean;
  faceUp: boolean;
}

export interface GameState {
  level: number;
  phase: GamePhase;
  moves: number;
  matchedPairs: number;
  pairCount: number;
  pairs: Pair[];
  cards: Card[];
  open: number[];
  restarts: number;
}

export function createGame(level: number, restarts = 0): GameState {
  const deal = generateLevel(level);
  return {
    level: deal.level,
    phase: "playing",
    moves: 0,
    matchedPairs: 0,
    pairCount: deal.pairCount,
    pairs: deal.pairs.map((country) => ({ id: country.id, country })),
    cards: deal.cards.map((card) => ({ ...card, matched: false, faceUp: false })),
    open: [],
    restarts: Math.max(0, Math.floor(restarts)),
  };
}

/** A pair attempt consists of one country-card reveal and one capital-card reveal. */
export function selectCard(state: GameState, index: number): GameState {
  if (state.phase !== "playing" || !Number.isInteger(index) || index < 0 || index >= state.cards.length) return state;
  const chosen = state.cards[index]!;
  if (chosen.matched || chosen.faceUp) return state;

  let cards = state.cards;
  let open = state.open;
  if (open.length === 2) {
    // A fresh tap during the 700 ms mismatch pause closes the old pair immediately.
    const [first, second] = open;
    cards = cards.map((card) => (card.index === first || card.index === second ? { ...card, faceUp: false } : card));
    open = [];
  }

  const revealed = { ...chosen, faceUp: true };
  cards = cards.map((card) => (card.index === index ? revealed : card));
  if (open.length === 0) return { ...state, cards, open: [index] };

  const firstIndex = open[0]!;
  const first = cards[firstIndex]!;
  const moves = state.moves + 1;
  if (first.pairId === revealed.pairId && first.kind !== revealed.kind) {
    cards = cards.map((card) => (card.index === firstIndex || card.index === index ? { ...card, faceUp: true, matched: true } : card));
    const matchedPairs = state.matchedPairs + 1;
    return { ...state, cards, open: [], moves, matchedPairs, phase: matchedPairs === state.pairCount ? "won" : "playing" };
  }
  return { ...state, cards, open: [firstIndex, index], moves };
}

/** Close a pending mismatch. A later tap may do this earlier through selectCard(). */
export function closeOpenCards(state: GameState): GameState {
  if (state.open.length !== 2 || state.phase !== "playing") return state;
  const open = new Set(state.open);
  return { ...state, open: [], cards: state.cards.map((card) => (open.has(card.index) ? { ...card, faceUp: false } : card)) };
}

export function starsFor(moves: number, pairs: number): 1 | 2 | 3 {
  if (moves <= pairs + Math.ceil(pairs * 0.5)) return 3;
  if (moves <= pairs * 2) return 2;
  return 1;
}

export function starTarget(pairs: number): number {
  return pairs + Math.ceil(pairs * 0.5);
}

/** Rebuild a run from its deterministic level and ordered card taps. */
export function replay(level: number, taps: readonly number[]): GameState {
  let state = createGame(level);
  for (const index of taps) state = selectCard(state, index);
  return state;
}

export function levelFrom(deal: GeneratedLevel): number {
  return deal.level;
}
